import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Clock,
  Folder,
  FolderPlus,
  KanbanSquare,
  Lock,
  MoreHorizontal,
  Pencil,
  Search,
  SearchX,
  Star,
  Trash2,
  Users,
} from 'lucide-react'
import { boardsApi, foldersApi, type Board, type BoardFolder, type BoardInput, type FolderScope } from '@/api/boardsApi'
import { BoardDialog } from '@/components/board/BoardDialog'
import { BoardIcon } from '@/components/board/BoardIcon'
import { BoardMenu } from '@/components/board/BoardMenu'
import { FolderDialog } from '@/components/board/FolderDialog'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { EmptyState, ErrorState, LoadingState, errorMessage } from '@/components/common/States'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useBoardActions } from '@/hooks/useBoardActions'
import { useArchivedBoards, useBoardFolders, useBoardMutation, useBoards } from '@/hooks/useBoards'
import { boardTree, matchBoards } from '@/lib/boardTree'
import { cn } from '@/lib/utils'

type FolderEdit = { mode: 'create'; scope: FolderScope } | { mode: 'rename'; folder: BoardFolder } | null

/**
 * /boards — every board I can see, organised: starred and recent first, then
 * my team's folders, my folders and the boards in none. `?archived=1` lists
 * the archived ones; `?new=1` opens the new-board dialog.
 */
export default function BoardsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const showArchived = params.get('archived') === '1'
  const boards = useBoards()
  const archived = useArchivedBoards(showArchived)
  const folders = useBoardFolders()
  const actions = useBoardActions(folders.data)
  const [q, setQ] = useState('')

  const creating = params.get('new') === '1'
  const setCreating = (open: boolean) => {
    const next = new URLSearchParams(params)
    if (open) next.set('new', '1')
    else next.delete('new')
    setParams(next, { replace: true })
  }
  const create = useBoardMutation((input: BoardInput) => boardsApi.create(input))

  const [folderEdit, setFolderEdit] = useState<FolderEdit>(null)
  const [deleting, setDeleting] = useState<BoardFolder | null>(null)
  const createFolder = useBoardMutation(foldersApi.create)
  const renameFolder = useBoardMutation(foldersApi.rename)
  const removeFolder = useBoardMutation(foldersApi.remove)
  const orderFolders = useBoardMutation(foldersApi.order)
  const folderWrite = folderEdit?.mode === 'rename' ? renameFolder : createFolder

  const tree = useMemo(
    () => boardTree(boards.data ?? [], folders.data?.personal ?? [], folders.data?.team?.folders ?? []),
    [boards.data, folders.data],
  )
  const team = folders.data?.team ?? null
  const searching = q.trim().length > 0
  const matches = matchBoards(boards.data ?? [], q)

  const moveFolder = (list: BoardFolder[], scope: FolderScope, id: string, by: -1 | 1) => {
    const ids = list.map((f) => f.id)
    const i = ids.indexOf(id)
    const j = i + by
    if (i < 0 || j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    orderFolders.mutate({ scope, folderIds: ids })
  }

  const card = (b: Board) => <BoardTile key={b.id} board={b} folders={folders.data} actions={actions} />
  const grid = (list: Board[]) => (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">{list.map(card)}</ul>
  )
  const folderSection = (f: BoardFolder, list: Board[], scope: FolderScope, siblings: BoardFolder[], index: number) => (
    <Group
      key={f.id}
      icon={scope === 'team' ? Users : Folder}
      title={f.name}
      badge={scope === 'team' ? t('boards.folders.team') : undefined}
      count={list.length}
      menu={
        <FolderMenu
          folder={f}
          first={index === 0}
          last={index === siblings.length - 1}
          onRename={() => setFolderEdit({ mode: 'rename', folder: f })}
          onDelete={() => setDeleting(f)}
          onMove={(by) => moveFolder(siblings, scope, f.id, by)}
        />
      }
    >
      {list.length ? grid(list) : <p className="rounded-lg border border-dashed border-line-strong px-4 py-5 text-center text-meta text-ink-muted">{t('boards.folders.empty')}</p>}
    </Group>
  )

  const newFolder = (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-1.5">
          <FolderPlus className="size-4" aria-hidden />
          {t('boards.folders.new')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-1">
        <MenuButton icon={Folder} title={t('boards.folders.newMine')} help={t('boards.folders.newMineHelp')} onClick={() => setFolderEdit({ mode: 'create', scope: 'personal' })} />
        <MenuButton
          icon={Users}
          title={t('boards.folders.newTeam')}
          help={team ? t('boards.folders.newTeamHelp', { team: team.name }) : t('boards.folders.noTeam')}
          disabled={!team}
          onClick={() => setFolderEdit({ mode: 'create', scope: 'team' })}
        />
      </PopoverContent>
    </Popover>
  )
  const newButton = <Button onClick={() => setCreating(true)}>{t('boards.new')}</Button>

  const body = () => {
    if (showArchived) {
      if (archived.isPending) return <LoadingState />
      if (archived.isError) return <ErrorState message={errorMessage(archived.error)} onRetry={() => void archived.refetch()} />
      return archived.data.length ? grid(archived.data) : <EmptyState icon={Archive} message={t('boards.archivedEmpty')} />
    }
    if (boards.isPending || folders.isPending) return <LoadingState />
    if (boards.isError) return <ErrorState message={errorMessage(boards.error)} onRetry={() => void boards.refetch()} />
    if (boards.data.length === 0 && !(folders.data?.personal.length || team?.folders.length))
      return (
        <div className="rounded-lg border border-line bg-background">
          <EmptyState icon={KanbanSquare} title={t('boards.emptyTitle')} message={t('boards.empty')} action={newButton} />
        </div>
      )
    if (searching)
      return matches.length ? grid(matches) : <EmptyState icon={SearchX} message={t('boards.noMatch', { q: q.trim() })} />
    return (
      <div className="flex flex-col gap-8">
        {tree.starred.length ? (
          <Group icon={Star} title={t('boards.starredTitle')} count={tree.starred.length}>
            {grid(tree.starred)}
          </Group>
        ) : null}
        {tree.recent.length ? (
          <Group icon={Clock} title={t('boards.recent')}>
            <ul className="flex flex-wrap gap-2">
              {tree.recent.map((b) => (
                <li key={b.id}>
                  <Link
                    to={`/boards/${b.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-line bg-background px-3 py-1.5 text-meta text-ink hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <BoardIcon icon={b.icon} color={b.color} className="size-3.5" />
                    {b.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Group>
        ) : null}
        {tree.team.map((n, i) => folderSection(n.folder as BoardFolder, n.boards, 'team', team?.folders ?? [], i))}
        {tree.personal.map((n, i) => folderSection(n.folder as BoardFolder, n.boards, 'personal', folders.data?.personal ?? [], i))}
        {tree.unfiled.length ? (
          <Group icon={KanbanSquare} title={t('boards.unfiled')} count={tree.unfiled.length}>
            {grid(tree.unfiled)}
          </Group>
        ) : null}
      </div>
    )
  }

  return (
    <Page>
      <PageHeading
        description={t('boards.eyebrow')}
        title={showArchived ? t('boards.archivedTitle') : t('boards.title')}
        actions={
          showArchived ? (
            <Button variant="outline" className="gap-1.5" onClick={() => setParams({}, { replace: true })}>
              <ArrowLeft className="size-4" aria-hidden />
              {t('boards.backToBoards')}
            </Button>
          ) : (
            <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto">
              <div className="relative w-full sm:w-60">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-placeholder" aria-hidden />
                <Input type="search" aria-label={t('boards.search')} placeholder={t('boards.search')} className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <Button variant="ghost" className="gap-1.5" onClick={() => setParams({ archived: '1' }, { replace: true })}>
                <Archive className="size-4" aria-hidden />
                {t('boards.showArchived')}
              </Button>
              {newFolder}
              {newButton}
            </div>
          )
        }
      />
      {body()}

      <BoardDialog
        open={creating}
        board={null}
        pending={create.isPending}
        error={create.error}
        onOpenChange={(o) => {
          setCreating(o)
          if (!o) create.reset()
        }}
        onSubmit={(input) => create.mutate(input, { onSuccess: (b) => navigate(`/boards/${b.id}`) })}
      />
      <FolderDialog
        open={!!folderEdit}
        folder={folderEdit?.mode === 'rename' ? folderEdit.folder : null}
        team={team}
        defaultScope={folderEdit?.mode === 'create' ? folderEdit.scope : 'personal'}
        pending={folderWrite.isPending}
        error={folderWrite.error}
        onOpenChange={(o) => {
          if (o) return
          setFolderEdit(null)
          createFolder.reset()
          renameFolder.reset()
        }}
        onSubmit={({ name, scope }) => {
          const done = { onSuccess: () => setFolderEdit(null) }
          if (folderEdit?.mode === 'rename') renameFolder.mutate({ id: folderEdit.folder.id, name }, done)
          else createFolder.mutate({ name, scope }, done)
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        title={t('boards.folders.deleteTitle', { name: deleting?.name ?? '' })}
        body={t('boards.folders.deleteBody')}
        confirmLabel={t('boards.folders.delete')}
        destructive
        pending={removeFolder.isPending}
        error={removeFolder.error ? errorMessage(removeFolder.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            removeFolder.reset()
          }
        }}
        onConfirm={() => deleting && removeFolder.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
      />
    </Page>
  )
}

function Group({ icon: Icon, title, badge, count, menu, children }: {
  icon: typeof Folder
  title: string
  badge?: string
  count?: number
  menu?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <header className="group/folder flex items-center gap-2">
        <Icon className="size-4 text-ink-muted" aria-hidden />
        <h2 className="text-body font-semibold text-ink-strong">{title}</h2>
        {badge ? <span className="rounded-sm bg-accent px-1.5 py-px text-label font-medium text-accent-foreground">{badge}</span> : null}
        {typeof count === 'number' ? <span className="text-meta text-ink-muted">{t('boards.count', { count })}</span> : null}
        {menu ? <span className="ml-auto opacity-60 group-hover/folder:opacity-100 has-[[data-state=open]]:opacity-100">{menu}</span> : null}
      </header>
      {children}
    </section>
  )
}

function MenuButton({ icon: Icon, title, help, disabled, onClick }: {
  icon: typeof Folder
  title: string
  help?: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-start gap-2.5 rounded-sm px-2.5 py-2 text-left hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-ink-muted" aria-hidden />
      <span className="flex flex-col">
        <span className="text-meta font-medium text-ink">{title}</span>
        {help ? <span className="text-label text-ink-muted">{help}</span> : null}
      </span>
    </button>
  )
}

function FolderMenu({ folder, first, last, onRename, onDelete, onMove }: {
  folder: BoardFolder
  first: boolean
  last: boolean
  onRename: () => void
  onDelete: () => void
  onMove: (by: -1 | 1) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const run = (fn: () => void) => () => {
    setOpen(false)
    fn()
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('boards.folders.menu', { name: folder.name })}
          className="rounded-md p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-1">
        <MenuButton icon={Pencil} title={t('boards.folders.rename')} onClick={run(onRename)} />
        <MenuButton icon={ArrowUp} title={t('boards.folders.moveUp', { name: folder.name })} disabled={first} onClick={run(() => onMove(-1))} />
        <MenuButton icon={ArrowDown} title={t('boards.folders.moveDown', { name: folder.name })} disabled={last} onClick={run(() => onMove(1))} />
        <div className="my-1 border-t border-line" />
        <MenuButton icon={Trash2} title={t('boards.folders.delete')} onClick={run(onDelete)} />
      </PopoverContent>
    </Popover>
  )
}

/** A board as a tile: its icon and colour, name, description, how it groups and who sees it; star and menu on the right. */
function BoardTile({ board: b, folders, actions }: {
  board: Board
  folders: Parameters<typeof BoardMenu>[0]['folders']
  actions: ReturnType<typeof useBoardActions>
}) {
  const { t } = useTranslation()
  return (
    <li className="group relative">
      <Link
        to={`/boards/${b.id}`}
        className="flex h-full flex-col gap-2 rounded-lg border border-line bg-background p-4 pr-16 transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-2">
          <BoardIcon icon={b.icon} color={b.color} />
          <span className="truncate text-body font-semibold text-ink-strong">{b.name}</span>
        </span>
        {b.description ? <span className="line-clamp-2 text-meta text-ink-body">{b.description}</span> : null}
        <span className="text-label text-ink-muted">
          {t('boards.groupBy')}: {t(`boards.groups.${b.groupBy}`)}
        </span>
        <span className="mt-auto flex items-center gap-1.5 text-label text-ink-muted">
          {b.visibility === 'private' ? <Lock className="size-3.5" aria-hidden /> : <Users className="size-3.5" aria-hidden />}
          {b.mine ? (b.visibility === 'private' ? t('boards.private') : t('boards.shared')) : t('boards.sharedBy', { name: b.owner.name })}
        </span>
      </Link>
      <div className="absolute right-2 top-2 flex items-center gap-0.5">
        {b.archivedAt ? null : (
          <button
            type="button"
            onClick={() => actions.toggleStar(b)}
            aria-pressed={b.starred}
            aria-label={b.starred ? t('boards.unstar', { name: b.name }) : t('boards.star', { name: b.name })}
            title={b.starred ? t('boards.unstar', { name: b.name }) : t('boards.star', { name: b.name })}
            className={cn(
              'rounded-md p-1 hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              b.starred ? 'text-sla-warning' : 'text-ink-faint opacity-0 hover:text-ink group-hover:opacity-100 focus-visible:opacity-100',
            )}
          >
            <Star className={cn('size-4', b.starred && 'fill-current')} aria-hidden />
          </button>
        )}
        <BoardMenu board={b} folders={folders} actions={actions} className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100" />
      </div>
    </li>
  )
}
